import cx from 'classnames'
import { useMemo } from 'react'
import CustomPhotosHeaderSlidingContent, {
  PhotoWithOrientation,
} from './CustomPhotosHeaderSlidingContent'
import staticHeaderCutoutHoliday from '../assets/landing/header-cutout-holiday.png'
import staticHeaderCutoutNewYear from '../assets/landing/header-cutout-new-year.png'
import staticHeaderCutoutRegular from '../assets/landing/header-cutout-regular.png'
import staticHeaderHoliday from '../assets/landing/header-static-holiday.png'
import staticHeaderNewYear from '../assets/landing/header-static-new-year.png'
import staticHeaderRegular from '../assets/landing/header-static-regular.png'
import { CuratedPhotoForPhotoProducts, FrameConfiguration } from '../types'
import { getHeaderType } from '../utils/headerType'

// Holiday configuration
const HOLIDAY_WIDTH = 780
const HOLIDAY_HEIGHT = 800

const DPF_JP_FRAMES_HOLIDAY: FrameConfiguration = {
  width: HOLIDAY_WIDTH,
  height: HOLIDAY_HEIGHT,
  requiredPhotos: 6,
  frames: [
    {
      left: (172 / HOLIDAY_WIDTH) * 100,
      top: (374 / HOLIDAY_HEIGHT) * 100,
      width: (430 / HOLIDAY_WIDTH) * 100,
      height: (272 / HOLIDAY_HEIGHT) * 100,
      rotate: 0,
    },
  ],
}

// Regular/New-Year configuration (same values for both)
const REGULAR_WIDTH = 780
const REGULAR_HEIGHT = 770

const DPF_JP_FRAMES_REGULAR: FrameConfiguration = {
  width: REGULAR_WIDTH,
  height: REGULAR_HEIGHT,
  requiredPhotos: 6,
  frames: [
    {
      left: (168 / REGULAR_WIDTH) * 100,
      top: (374 / REGULAR_HEIGHT) * 100,
      width: (435 / REGULAR_WIDTH) * 100,
      height: (273 / REGULAR_HEIGHT) * 100,
      rotate: 0,
    },
  ],
}

export default function CuratedPhotosHeader({
  curatedPhotos,
  isUsingFallback,
  frameOffsets,
  cutoutImageClassName,
}: {
  curatedPhotos: CuratedPhotoForPhotoProducts[]
  isUsingFallback: boolean
  frameOffsets?: Array<{
    left: number
    top: number
    scale?: number
    rotate?: number
    zIndex?: number
  }>
  cutoutImageClassName?: string
}) {
  const photos: PhotoWithOrientation[] = useMemo(
    () =>
      isUsingFallback
        ? []
        : curatedPhotos.map(photo => ({
            url: photo.croppedUrl,
            isLandscape:
              !!photo.imageWidth && !!photo.imageHeight && photo.imageWidth > photo.imageHeight,
          })),
    [isUsingFallback, curatedPhotos],
  )

  const headerType = useMemo(() => getHeaderType(), [])

  const frameConfiguration = useMemo(() => {
    if (headerType === 'holiday') {
      return DPF_JP_FRAMES_HOLIDAY
    } else {
      // regular and new-year use the same configuration
      return DPF_JP_FRAMES_REGULAR
    }
  }, [headerType])

  const staticHeaderImage = useMemo(() => {
    switch (headerType) {
      case 'new-year':
        return staticHeaderNewYear
      case 'regular':
        return staticHeaderRegular
      case 'holiday':
      default:
        return staticHeaderHoliday
    }
  }, [headerType])

  const cutoutHeaderImage = useMemo(() => {
    switch (headerType) {
      case 'new-year':
        return staticHeaderCutoutNewYear
      case 'regular':
        return staticHeaderCutoutRegular
      case 'holiday':
      default:
        return staticHeaderCutoutHoliday
    }
  }, [headerType])

  // Use requiredPhotos from frame configuration
  if (photos.length < frameConfiguration.requiredPhotos) {
    return (
      <img
        src={staticHeaderImage}
        className={cx('w-full h-full object-cover', cutoutImageClassName)}
        loading='eager'
        width={frameConfiguration.width}
        height={frameConfiguration.height}
        style={{ aspectRatio: `${frameConfiguration.width}/${frameConfiguration.height}` }}
        alt='Header image'
      />
    )
  }

  return (
    <CustomPhotosHeaderSlidingContent
      photos={photos}
      frameOffsets={frameOffsets}
      cutoutImageClassName={cutoutImageClassName}
      cutoutImage={cutoutHeaderImage}
      frameConfiguration={frameConfiguration}
    />
  )
}
